import { useContext, useEffect, useState } from "react"
import AuthContext from "store/context/PublicContext"
import translatesObj from "overrides/theme/entel/base/translates/translation.json"
import { fetchPolygons } from "services/polygon_services";
import { useLocation } from "react-router-dom";
import useSearchMiddlewareOSRM from "hooks/useSearchMiddlewareOSRM";
import { LocationsContext } from "store/context/LocationsContext";

interface ISelectServiceOptions {
  field: string
}

interface IOptionSelect {
  id: any
  label: string
  value: string
  courier?: any
  polygon?: any
}

const statussesTranslates: any = translatesObj

const statusses = [
  "CONFIRMED",
  "ASSIGNED",
  "IN_PREPARATION",
  "PICKING_ID_GENERATED",
  "PICKING_IN_PROGRESS",
  "VALIDATE_PICKING",
  "PICKING_CONFIRMED",
  "PICKING_COMPLETED_PENDING_PACKAGE",
  "PACKED_AND_READY_FOR_PICKUP",
  "DELIVERED_TO_COURIER",
  "DELIVER_TO_COURIER",
  "IN_TRANSIT",
  "ORDER_RECEIVED",
  "DELIVERED",
  "DELIVERED_IN_STORE",
  "READY_FOR_PICKUP",
  "PICKUP_AVAILABLE",
  "SUCCESSFUL_PAYMENT",
  "PICKED_UP",
  "CANCELED",
  "DELIVERED_IN_SOURCE",
  "ORDER_RETURNED",
]

const useSelectServiceOptions = ({ field }: ISelectServiceOptions) => {
  const [options, set_options] = useState<Array<IOptionSelect>>([])
  const { data: data_delivery_types, search_osrm_action: search_osrm_action_delivery_types, loading_app: loading_app_delivery_types } = useSearchMiddlewareOSRM({
    entity: "schedules",
  })
  const { data: data_channels, search_osrm_action: search_osrm_action_channels, loading_app: loading_app_channels } = useSearchMiddlewareOSRM({
    entity: "channels",
  })
  const { data: data_couriers, search_osrm_action: search_osrm_action_couriers, loading_app: loading_app_couriers } = useSearchMiddlewareOSRM({
    entity: "couriers",
  })

  const { regions, communes, setSelectedRegion, selectedCommune, setSelectedCommune, selectedCouriers, setSelectedCouriers, selectedPolygonType, setSelectedPolygonType, country, setSelectedPolygon, polygonsRefreshTrigger, setDisplayedPolygons, allPolygons, isLoadingPolygons, isLoadingRegions, isLoadingCommunes } = useContext(LocationsContext);
  const { pathname } = useLocation();

  const { user: { token } } = useContext(AuthContext)
  const [isLoading, setIsLoading] = useState(false);

  // Sync with relevant loading states based on the field
  useEffect(() => {
    let loading = false;
    if (field === "deliveryType") loading = loading_app_delivery_types;
    if (field === "channel") loading = loading_app_channels;
    if (field === "courier") loading = loading_app_couriers;
    if (field === "region") loading = isLoadingRegions;
    if (field === "commune") loading = isLoadingCommunes;
    if (field === "polygon") loading = isLoadingPolygons;
    
    setIsLoading(loading);
  }, [
    field,
    loading_app_delivery_types,
    loading_app_channels,
    loading_app_couriers,
    isLoadingPolygons,
    isLoadingRegions,
    isLoadingCommunes,
  ]);

  useEffect(() => {
    if (field === "region") {
      set_options(regions.map((region: string, _i: number) => ({
        id: _i,
        label: region,
        value: region,
      })));
    }
    if (field === "commune") {
      set_options(communes.map((commune: any, _i: number) => ({
        id: _i,
        label: commune?.name || commune,
        value: commune?.name || commune,
        commune: commune
      })));
    }

    if (field === "currentState") {
      set_options(
        statusses.map((state: string, _i: number) => ({
          id: _i,
          label: statussesTranslates[state]?.title || state,
          value: state,
        })),
      )
    }
    if (field === "deliveryType") {
      search_osrm_action_delivery_types({ from: 0, size: 1000, fields: ["id", "deliveryType"] })
    }
    if (field === "channel") {
      search_osrm_action_channels({ from: 0, size: 1000, fields: ["id", "name"] })
    }


    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field, regions, communes, selectedCommune, selectedCouriers, selectedPolygonType, token, pathname, allPolygons])

  useEffect(() => {
    if (field === "courier") {
      search_osrm_action_couriers({ from: 0, size: 1000, fields: ["id", "name", "alias"] })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field, token, country])

  useEffect(() => {
    if (field === "polygonType") {
      set_options([
        { id: 0, label: "Todos", value: "all" },
        { id: 1, label: "Inclusión", value: "include" },
        { id: 2, label: "Exclusión", value: "exclude" }
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field])

  useEffect(() => {
    if (field === "polygon") {
      if (selectedPolygonType || selectedCommune) {
        const loadPolygons = async () => {
          let records = [];

          // Use allPolygons from context if available (optimistic/local state), otherwise fetch
          if (pathname === "/coverages/map") {
            // If we are on the map screen, we should rely on the main screen to fetch polygons to avoided duplicate calls
            // We use allPolygons to ensure we have the full list, not the filtered one displayed on map
            records = allPolygons || [];
          } else {
            // For other screens, fetch (though this case might need review for multi-select support if used elsewhere)
            const courierIds = selectedCouriers?.length > 0 ? selectedCouriers.map((c: any) => c.recordIds).flat() : null;
            setIsLoading(true);
            const res = await fetchPolygons(courierIds, token, "all", selectedCommune);
            records = res.records || [];
            setIsLoading(false);
          }

          // Filter client-side based on selectedPolygonType
          // Filter client-side based on selectedPolygonType
          const filteredData = selectedPolygonType && selectedPolygonType !== "all"
            ? records.filter((polygon: any) => polygon.type?.toLowerCase() === selectedPolygonType.toLowerCase())
            : records;

          const newOptions = filteredData.map((polygon: any, _i: number) => {
            return {
              id: _i,
              label: polygon.name,
              value: polygon.id,
              polygon: polygon,
            };
          });

          newOptions.unshift({
            id: -1,
            label: "Todos",
            value: "all_polygons",
            polygon: null
          });

          set_options(newOptions);
        }
        loadPolygons();
      } else {
        set_options([])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field, selectedCouriers, selectedPolygonType, token, polygonsRefreshTrigger, selectedCommune, allPolygons])

  useEffect(() => {
    if (data_delivery_types?.total) {
      set_options(
        data_delivery_types?.records?.map((delivery: any, _i: number) => ({
          id: _i,
          label: delivery?.deliveryType,
          value: delivery?.deliveryType,
        })),
      )
    }
    if (data_channels?.total) {
      set_options(
        data_channels?.records?.map((channel: any, _i: number) => ({
          id: _i,
          label: channel?.name,
          value: channel?.id,
        })),
      )
    }
    if (data_couriers?.total) {
      set_options(
        data_couriers?.records?.map((courier: any, _i: number) => ({
          id: _i,
          label: pathname === "/coverages/map" ? `${courier.name} (${courier.alias || courier.id})` : courier.name,
          value: courier.id,
          courier: courier
        })),
      )
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data_delivery_types, data_channels, data_couriers, field])

  return {
    options,
    isLoading,
    setSelectedRegion,
    setSelectedCommune,
    setSelectedCouriers,
    setSelectedPolygonType,
    setSelectedPolygon,
    setDisplayedPolygons,
  }
}

export default useSelectServiceOptions
